import { useEffect, useState, useContext } from "react";
import { Container, Row, Col } from "react-bootstrap";
import SubscribeDirect from "components/subscriptions/SubscribeDirect";
import SubscriptionBalanceDetails from "components/subscriptions/SubscriptionBalanceDetails";
import TokenSpending from "components/subscriptions/TokenSpending";
import { AppContext } from "context/appContext";
import {
    updateSubscriptionStatus,
    storeSubscriptionAction,
    storeSubscriptionID,
} from "store/actions";
import moment from "moment";
import {
    deleteUnpaidSubscription,
    getAllSubscription,
} from "service/subscriptionService";
import {
    ACTION_FAIL_MESSAGE,
    FESubscriptionStatus,
    PlanType,
    SubscriptionStatus,
} from "constant";
import { useSubscriptionContext } from "context/subscriptionContext";
import {
    SubscriptionPlanType,
    getSubscriptionPlanType,
} from "components/subscriptions/CurrentPlanInformation";
import withSubscriptionPermissionCheck from "components/hoc/SubscriptionPermissionCheck";
import { orderBy } from "lodash";
import { showErrorAlert } from "utils/alert";

const Subscriptions = () => {
    const { storeDispatchActions } = useContext(AppContext);
    const [allSubscription, setAllSubscription]: any = useState({});
    const [subscriptionStatus, setSubscriptionStatus]: any = useState("");
    const [billingEndDate, setBillingEndDate] = useState(moment());
    const { setSubscription }: any = useSubscriptionContext();

    const latestSubscription = orderBy(
        allSubscription,
        ["create_time"],
        ["desc"]
    )[0];
    const subscriptionType = getSubscriptionPlanType({
        is_prepaid: latestSubscription?.customer?.is_prepaid,
        is_cancellable: latestSubscription?.customer?.is_cancellable,
    });

    useEffect(() => {
        if (!allSubscription.length) return;
        (async () => {
            for (const subscription of allSubscription) {
                if (
                    subscription.type_ === PlanType.SUBSCRIPTION &&
                    subscription.status === SubscriptionStatus.CREATED
                ) {
                    await deleteUnpaidSubscription(subscription.uuid);
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSubscription.length]);

    useEffect(() => {
        (async () => {
            const allSubscriptions = await getAllSubscription();
            if (allSubscriptions === null) {
                showErrorAlert({
                    message: ACTION_FAIL_MESSAGE,
                });
            }
            if (allSubscriptions.length) {
                const sortedSubscriptionHistory = [...allSubscriptions].sort(
                    (a: any, b: any) =>
                        b.create_time.localeCompare(a.create_time)
                );
                setAllSubscription(sortedSubscriptionHistory);
                storeDispatchActions(
                    storeSubscriptionAction(sortedSubscriptionHistory)
                );
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setSubscription(latestSubscription);
        let billingEndDate = moment(
            latestSubscription?.next_billing_date
        ).endOf("day");
        switch (latestSubscription?.status) {
            case SubscriptionStatus.ACTIVE:
                setSubscriptionStatus(FESubscriptionStatus.SUBSCRIBED);
                storeDispatchActions(
                    updateSubscriptionStatus({
                        isSubscribed: true,
                        hasSubscribed: true,
                    })
                );
                storeDispatchActions(
                    storeSubscriptionID(latestSubscription.uuid)
                );

                setBillingEndDate(
                    moment(latestSubscription.billing_period_end_date)
                );
                break;
            case SubscriptionStatus.CANCELED:
                setBillingEndDate(billingEndDate);

                setSubscriptionStatus(
                    moment() <= billingEndDate
                        ? FESubscriptionStatus.PENDING_CANCEL
                        : FESubscriptionStatus.CANCELLATION_PERIOD_ENDED
                );

                storeDispatchActions(
                    updateSubscriptionStatus({
                        isSubscribed: moment() <= billingEndDate,
                        hasSubscribed: true,
                    })
                );
                break;
            case SubscriptionStatus.EXPIRED:
            case SubscriptionStatus.PAST_DUE:
                setSubscriptionStatus(
                    latestSubscription?.status === SubscriptionStatus.EXPIRED
                        ? FESubscriptionStatus.EXPIRED
                        : FESubscriptionStatus.PAST_DUE
                );
                storeDispatchActions(
                    updateSubscriptionStatus({
                        isSubscribed: false,
                        hasSubscribed: true,
                    })
                );

                setBillingEndDate(billingEndDate);
                break;
            default:
                setSubscriptionStatus(FESubscriptionStatus.UNSUBSCRIBED);
                storeDispatchActions(
                    updateSubscriptionStatus({
                        isSubscribed: false,
                        hasSubscribed: false,
                    })
                );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSubscription.length, latestSubscription?.uuid]);

    const renderSubscriptionComponent = () => {
        if (!subscriptionStatus) return <></>;

        return subscriptionStatus === FESubscriptionStatus.UNSUBSCRIBED ? (
            <SubscribeDirect />
        ) : (
            <>
                <SubscriptionBalanceDetails
                    subscriptionStatus={subscriptionStatus}
                    currentSubscription={latestSubscription}
                    billingEndDate={billingEndDate}
                    allSubscription={allSubscription}
                    subscriptionType={subscriptionType}
                />
                {subscriptionType !== SubscriptionPlanType.BASIC && (
                    <TokenSpending />
                )}
            </>
        );
    };

    return (
        <>
            <div className="page-content subscription-page">
                <Container fluid>
                    <Row>
                        <Col>
                            <h5 className="page-title">Subscription</h5>
                        </Col>
                    </Row>
                </Container>

                {renderSubscriptionComponent()}
            </div>
        </>
    );
};

export default withSubscriptionPermissionCheck(Subscriptions);
